import React, { useState, useEffect } from 'react';
import { FaBox, FaCube, FaWeight, FaFileAlt, FaPlus, FaTrash, FaCheckCircle, FaTimes } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { removeBackground } from '@imgly/background-removal';
import * as submasterService from '../../services/submaster.service';
import { productService } from '../../services/product.service';
import './Product.css';

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const revokePreviewIfBlob = (preview) => {
  if (typeof preview === 'string' && preview.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(preview);
    } catch {
      /* ignore */
    }
  }
};

/** Uploaded row: new uploads use `file` + `preview`; API rows use `path` and no `file`. */
const getImageDisplaySrc = (img) => img?.preview || img?.path || '';

const SUBMASTER_TYPES = [
  { key: 'category', label: 'Category' },
  { key: 'product-name', label: 'Product Name' },
  { key: 'collection', label: 'Collection' },
  { key: 'materials', label: 'Materials' },
  { key: 'hs-code', label: 'HS Code' },
  { key: 'color', label: 'Color' },
  { key: 'finish', label: 'Finish' },
  { key: 'suitable-for', label: 'Suitable For' },
  { key: 'pack-type', label: 'Pack Type' },
];

const TagInput = ({ label, selectedIds, allItems, fieldName, onAdd, onRemove, required, errors }) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredItems = allItems.filter(item => 
    !selectedIds.includes(item.id) && 
    item.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleSelect = (id) => {
    onAdd(fieldName, id);
    setInputValue('');
    setShowDropdown(false);
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-600 label-dark">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <div className="tag-input-wrapper">
        {/* Selected Tags */}
        <div className="tag-chip-container">
          {selectedIds.length > 0 ? (
            selectedIds.map((id) => {
              const item = allItems.find(o => o.id === id);
              return item ? (
                <span key={id} className="tag-chip">
                  {item.name}
                  <FaTimes 
                    onClick={() => onRemove(fieldName, id)} 
                    className="tag-chip-remove" 
                  />
                </span>
              ) : null;
            })
          ) : (
            <small className="text-muted tag-chip-empty">No items selected</small>
          )}
        </div>

        {/* Input Field */}
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={`Search and add ${label.toLowerCase()}...`}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        />

        {/* Dropdown List */}
        {showDropdown && filteredItems.length > 0 && (
          <ul className="tag-dropdown">
            {filteredItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(item.id);
                  }}
                  className="tag-dropdown-btn"
                >
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {errors && <div className="invalid-feedback d-block text-error">{errors}</div>}
    </div>
  );
};

const emptyPackRow = () => ({
  quantityPack: '',
  length: '',
  breadth: '',
  height: '',
  netWt: '',
});

/** Maps API/legacy rows: supports `quantityPack` or former `unit` key. */
const normalizePackRow = (row) => ({
  quantityPack: row?.quantityPack ?? row?.quantity_pack ?? row?.unit ?? '',
  length: row?.length ?? '',
  breadth: row?.breadth ?? '',
  height: row?.height ?? '',
  netWt: row?.netWt ?? '',
});

const normalizePackRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [emptyPackRow()];
  return rows.map(normalizePackRow);
};

const INITIAL_FORM = {
  uid: '',
  itemNo: '',
  category: '',
  productName: '',
  collection: [],
  materials: [],
  hsCode: '',
  color: [],
  finish: [],
  suitableFor: [],
  packType: [],
  lengthDimension: '',
  breadthDimension: '',
  heightDimension: '',
  dimensionUnit: 'cm',
  netWeight: '',
  weightUnit: 'KG',
  moq: '',
  cost: '',
  remark: '',
  innerPacks: [emptyPackRow()],
  masterPacks: [emptyPackRow()],
};

const ProductMaster = () => {
  const [submasters, setSubmasters] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [processingImages, setProcessingImages] = useState(false);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const { uid } = useParams();
  const isEditMode = !!uid;

  const [formData, setFormData] = useState({
    uid: '',
    itemNo: '',
    category: '',
    productName: '',
    collection: [],
    materials: [],
    hsCode: '',
    color: [],
    finish: [],
    suitableFor: [],
    packType: [],
    lengthDimension: '',
    breadthDimension: '',
    heightDimension: '',
    dimensionUnit: 'cm',
    netWeight: '',
    weightUnit: 'KG',
    moq: '',
    cost: '',
    remark: '',
    innerPacks: [emptyPackRow()],
    masterPacks: [emptyPackRow()],
  });

  const [errors, setErrors] = useState({});

  // Generate UID on mount or load from editing product
  useEffect(() => {
    let mounted = true;

    const loadProduct = async () => {
      if (isEditMode && uid) {
        try {
          const editingProduct = await productService.getProductByUID(uid);
          if (editingProduct && mounted) {
            setFormData({
              uid: editingProduct.uid || '',
              itemNo: editingProduct.item_no || editingProduct.itemNo || '',
              category: editingProduct.category_id || editingProduct.category || '',
              productName: editingProduct.product_name_id || editingProduct.productName || '',
              collection: editingProduct.collection ? 
                (Array.isArray(editingProduct.collection) ? editingProduct.collection.map(c => c.id) : []) : [],
              materials: editingProduct.materials ? 
                (Array.isArray(editingProduct.materials) ? editingProduct.materials.map(m => m.id) : []) : [],
              hsCode: editingProduct.hs_code_id || editingProduct.hsCode || '',
              color: editingProduct.color ? 
                (Array.isArray(editingProduct.color) ? editingProduct.color.map(c => c.id) : []) : [],
              finish: editingProduct.finish ? 
                (Array.isArray(editingProduct.finish) ? editingProduct.finish.map(f => f.id) : []) : [],
              suitableFor: Array.isArray(editingProduct.suitableFor)
                ? editingProduct.suitableFor.map((s) => (typeof s === 'object' && s != null ? s.id : s))
                : editingProduct.suitableForId != null
                  ? [editingProduct.suitableForId]
                  : [],
              packType: editingProduct.pack_type ? 
                (Array.isArray(editingProduct.pack_type) ? editingProduct.pack_type.map(p => p.id) : []) : [],
              lengthDimension: editingProduct.length_dimension || editingProduct.lengthDimension || '',
              breadthDimension: editingProduct.breadth_dimension || editingProduct.breadthDimension || '',
              heightDimension: editingProduct.height_dimension || editingProduct.heightDimension || '',
              dimensionUnit: editingProduct.dimension_unit || editingProduct.dimensionUnit || 'cm',
              netWeight: editingProduct.net_weight || editingProduct.netWeight || '',
              weightUnit: editingProduct.weight_unit || editingProduct.weightUnit || 'KG',
              moq: editingProduct.moq || '',
              cost: editingProduct.cost || '',
              remark: editingProduct.remark || '',
              innerPacks: normalizePackRows(editingProduct.inner_packs || editingProduct.innerPacks),
              masterPacks: normalizePackRows(editingProduct.master_packs || editingProduct.masterPacks),
            });

            if (editingProduct.images && Array.isArray(editingProduct.images)) {
              setUploadedImages(
                editingProduct.images.map((img) => ({
                  ...img,
                  preview: img.preview || img.path,
                }))
              );
              const mainImageIdx = editingProduct.images.findIndex((img) => img.is_main);
              setMainImageIndex(mainImageIdx >= 0 ? mainImageIdx : 0);
            }
          } else if (mounted) {
            setMessage({
              type: 'error',
              text: `Product not found for UID: ${uid}`
            });
          }
        } catch (e) {
          console.error('Error loading product:', e);
          if (mounted) {
            setMessage({
              type: 'error',
              text: 'Error loading product data'
            });
          }
        }
      }
    };

    const genUID = async () => {
      const year = new Date().getFullYear().toString().slice(-2);
      try {
        const response = await fetch(`${process.env.REACT_BACKEND_BASE_URL}/api/v1/products/last-uid`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const lastNum = parseInt(data.uid.replace('AKP', ''), 10);
          const next = lastNum + 1;
          const padded = String(next).padStart(6, '0');
          return `AKP${year}${padded}`;
        } else {
          throw new Error('Could not fetch last UID from server');
        }
      } catch (e) {
        console.warn('Using fallback UID generation:', e);
        const timestamp = Date.now().toString().slice(-6);
        return `AKP${year}${timestamp}`;
      }
    };
    
    const initializeForm = async () => {
      if (!isEditMode) {
        const newUid = await genUID();
        if (mounted) {
          setFormData((f) => ({ ...f, uid: newUid }));
        }
      } else {
        loadProduct();
      }
    };

    const loadSubmasters = async () => {
      try {
        const results = {};
        await Promise.all(
          SUBMASTER_TYPES.map(async (t) => {
            try {
              const items = await submasterService.getItems(t.key);
              results[t.key] = Array.isArray(items) ? items : items.data || [];
            } catch (e) {
              console.error(`Failed to load ${t.key}`, e);
              results[t.key] = [];
            }
          })
        );
        if (!mounted) return;
        console.log('Submasters loaded:', results);
        setSubmasters(results);
      } catch (e) {
        console.error('Failed to load submasters', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    setLoading(true);
    initializeForm();
    loadSubmasters();

    return () => {
      mounted = false;
    };
  }, [isEditMode, uid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleMultiSelect = (fieldName, selectedId) => {
    setFormData((f) => {
      const current = f[fieldName] || [];
      const updated = current.includes(selectedId)
        ? current.filter((id) => id !== selectedId)
        : [...current, selectedId];
      return { ...f, [fieldName]: updated };
    });
    setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
  };

  const removeTag = (fieldName, tagId) => {
    setFormData((f) => ({
      ...f,
      [fieldName]: f[fieldName].filter((id) => id !== tagId),
    }));
  };

  const addPackRow = (packType) => {
    setFormData((f) => ({
      ...f,
      [packType]: [...f[packType], emptyPackRow()],
    }));
  };

  const removePackRow = (packType, index) => {
    setFormData((f) => ({
      ...f,
      [packType]: f[packType].filter((_, i) => i !== index),
    }));
  };

  const handleDelete = async () => {
    if (!isEditMode || !formData.uid) {
      setMessage({ type: 'danger', text: 'Cannot delete a new product' });
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete product "${formData.itemNo}" (UID: ${formData.uid})? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      // Find product by UID to get database ID
      const product = await productService.getProductByUID(formData.uid);
      if (!product) {
        setMessage({ type: 'danger', text: 'Product not found' });
        return;
      }

      await productService.deleteProduct(product.id);

      setMessage({
        type: 'success',
        text: `Product "${formData.itemNo}" deleted successfully!`
      });

      setTimeout(() => {
        window.location.href = '/dashboard/product/catalog';
      }, 1500);
    } catch (err) {
      console.error('Error deleting product:', err);
      setMessage({
        type: 'danger',
        text: 'Error deleting product. Please try again.'
      });
    }
  };

  const handlePackChange = (packType, index, field, value) => {
    setFormData((f) => ({
      ...f,
      [packType]: f[packType].map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const handleImageUpload = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []).filter((f) => f.type.startsWith('image/'));
    input.value = '';

    if (files.length === 0) return;

    if (uploadedImages.length + files.length > 5) {
      setMessage({ type: 'warning', text: 'Maximum 5 images allowed' });
      return;
    }

    setProcessingImages(true);
    setMessage(null);
    const newEntries = [];
    let anyBgRemovalFailed = false;

    try {
      for (const file of files) {
        let outFile = file;
        let preview;
        try {
          const blob = await removeBackground(file);
          const baseName = (file.name.replace(/\.[^.]+$/, '') || 'product').replace(/[^\w.-]+/g, '_');
          outFile = new File([blob], `${baseName}-nobg.png`, { type: 'image/png' });
          preview = URL.createObjectURL(outFile);
        } catch (err) {
          console.error('Background removal failed:', err);
          anyBgRemovalFailed = true;
          outFile = file;
          preview = await readFileAsDataUrl(file);
        }
        newEntries.push({ file: outFile, preview });
      }

      if (anyBgRemovalFailed) {
        setMessage({
          type: 'warning',
          text: 'Background could not be removed for one or more images; originals were kept.',
        });
      }

      setUploadedImages((prev) => [...prev, ...newEntries]);
    } catch (err) {
      console.error('Image upload processing error:', err);
      setMessage({ type: 'danger', text: 'Failed to process images. Please try again.' });
    } finally {
      setProcessingImages(false);
    }
  };

  const removeImage = (index) => {
    const prevLen = uploadedImages.length;
    const newLen = prevLen - 1;

    setUploadedImages((prev) => {
      const removed = prev[index];
      if (removed) revokePreviewIfBlob(removed.preview);
      return prev.filter((_, i) => i !== index);
    });

    setMainImageIndex((cur) => {
      if (newLen <= 0) return 0;
      if (index < cur) return cur - 1;
      if (index === cur) return Math.min(cur, newLen - 1);
      return cur >= newLen ? newLen - 1 : cur;
    });
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.itemNo || !formData.itemNo.trim()) errs.itemNo = 'Item No. is required';
    if (!formData.category) errs.category = 'Category is required';
    if (!formData.productName) errs.productName = 'Product Name is required';
    if (!formData.hsCode) errs.hsCode = 'HS Code is required';
    if (!formData.color || formData.color.length === 0) errs.color = 'At least one Color is required';
    if (!formData.finish || formData.finish.length === 0) errs.finish = 'At least one Finish is required';
    if (!formData.packType || formData.packType.length === 0) errs.packType = 'At least one Pack Type is required';
    if (!formData.lengthDimension) errs.lengthDimension = 'Length is required';
    if (!formData.breadthDimension) errs.breadthDimension = 'Breadth is required';
    if (!formData.heightDimension) errs.heightDimension = 'Height is required';
    if (!formData.netWeight) errs.netWeight = 'Net Weight is required';
    if (!formData.moq || isNaN(formData.moq)) errs.moq = 'Valid MOQ is required';
    if (!formData.cost || isNaN(formData.cost)) errs.cost = 'Valid Cost is required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    
    const v = validateForm();
    if (Object.keys(v).length) {
      setErrors(v);
      setMessage({ type: 'danger', text: 'Please fix the errors highlighted below.' });
      return;
    }

    if (processingImages) {
      setMessage({ type: 'warning', text: 'Please wait for image processing to finish.' });
      return;
    }

    setSaving(true);
    try {
      // Prepare data for API
      const productData = {
        itemNo: formData.itemNo,
        category: formData.category,
        productName: formData.productName,
        hsCode: formData.hsCode,
        suitableFor: formData.suitableFor,
        lengthDimension: formData.lengthDimension,
        breadthDimension: formData.breadthDimension,
        heightDimension: formData.heightDimension,
        dimensionUnit: formData.dimensionUnit,
        netWeight: formData.netWeight,
        weightUnit: formData.weightUnit,
        moq: formData.moq,
        cost: formData.cost,
        remark: formData.remark,
        mainImageIndex: mainImageIndex,
        color: formData.color,
        finish: formData.finish,
        material: formData.materials,
        collection: formData.collection,
        packType: formData.packType,
        innerPacks: formData.innerPacks,
        masterPacks: formData.masterPacks,
        images: uploadedImages.map(img => img.file),
      };

      let result;
      if (isEditMode) {
        // Update existing product
        const product = await productService.getProductByUID(formData.uid);
        if (!product) {
          throw new Error('Product not found');
        }
        result = await productService.updateProduct(product.id, productData);
        setMessage({ 
          type: 'success', 
          text: `Product updated successfully (UID: ${formData.uid})` 
        });
      } else {
        // Create new product
        result = await productService.createProduct(productData);
        setMessage({ 
          type: 'success', 
          text: `Product created successfully (UID: ${result.uid || formData.uid})` 
        });

        // Reset form for create mode
        const newUid = await genUID();
        setFormData({
          ...INITIAL_FORM,
          uid: newUid,
        });
        uploadedImages.forEach((img) => revokePreviewIfBlob(img.preview));
        setUploadedImages([]);
        setMainImageIndex(0);
      }

      // Scroll to top to see success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      setSaving(false);
    } catch (err) {
      console.error('Error saving product:', err);
      setMessage({ type: 'danger', text: err.message || 'Failed to save product. Please try again.' });
      setSaving(false);
    }
  };

  const genUID = async () => {
    const year = new Date().getFullYear().toString().slice(-2);
    try {
      const response = await fetch(`${process.env.REACT_BACKEND_BASE_URL}/api/v1/products/last-uid`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const lastNum = parseInt(data.uid.replace('AKP', ''), 10);
        const next = lastNum + 1;
        const padded = String(next).padStart(6, '0');
        return `AKP${year}${padded}`;
      } else {
        throw new Error('Could not fetch last UID from server');
      }
    } catch (e) {
      console.warn('Using fallback UID generation:', e);
      const timestamp = Date.now().toString().slice(-6);
      return `AKP${year}${timestamp}`;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="product-page-bg">
      <div className="container-lg mt-2">
        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="product-header-icon">
              <FaBox size={28} />
            </div>
            <div>
              <h1 className="mb-0 product-title">
                {isEditMode ? 'Edit Product' : 'Product Master'}
              </h1>
              <p className="mb-0 text-muted">
                {isEditMode ? `Editing: ${formData.uid}` : 'Create and manage product information'}
              </p>
            </div>
          </div>
        </div>

        {message && (
          <div className={`alert alert-${message.type} alert-dismissible fade show alert-rounded`} role="alert">
            <strong>{message.type === 'success' ? '✓ Success!' : message.type === 'warning' ? '⚠ Warning' : '✗ Error'}</strong> {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Product Images & Identification - Side by Side */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderTop: '4px solid #3498db' }}>
            <div className="card-body">
              <div className="row">
                {/* Images Section - Left Side */}
                <div className="col-lg-3 mb-3 mb-lg-0">
                  <h6 style={{ color: '#3498db', fontWeight: '600', marginBottom: '12px' }}>Product Images</h6>
                  
                  {/* Main Image */}
                  {uploadedImages.length > 0 && (
                    <div style={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '3px solid #667eea',
                      backgroundColor: '#f8f9fa',
                      marginBottom: '10px'
                    }}>
                      <img
                        src={getImageDisplaySrc(uploadedImages[mainImageIndex])}
                        alt="Main Product"
                        style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                      />
                    </div>
                  )}

                  {/* Upload Input */}
                  <input
                    type="file"
                    className="form-control form-control-sm"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadedImages.length >= 5 || processingImages}
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', marginBottom: '8px' }}
                  />
                  <small className="text-muted d-block" style={{ marginBottom: '8px' }}>
                    {uploadedImages.length}/5 images
                    {processingImages && (
                      <span className="ms-2 text-primary fw-semibold">Removing backgrounds…</span>
                    )}
                  </small>
                  {!processingImages && (
                    <small className="text-muted d-block" style={{ marginBottom: '8px', fontSize: '0.75rem' }}>
                      Background is removed in the browser before upload (PNG). First run may download the AI model.
                    </small>
                  )}

                  {/* Thumbnails */}
                  {uploadedImages.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', overflowX: 'auto' }}>
                      {uploadedImages.map((img, idx) => (
                        <div
                          key={idx}
                          style={{
                            position: 'relative',
                            flexShrink: 0,
                            cursor: 'pointer'
                          }}
                          onClick={() => setMainImageIndex(idx)}
                        >
                          <img
                            src={getImageDisplaySrc(img)}
                            alt={`Thumb ${idx + 1}`}
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border: mainImageIndex === idx ? '2px solid #667eea' : '1px solid #e0e0e0'
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              padding: '2px 5px',
                              background: '#dc3545',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              fontSize: '0.65rem',
                              lineHeight: '1',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(idx);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Product Identification - Right Side */}
                <div className="col-lg-9">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>UID/PID</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={formData.uid}
                        readOnly
                        style={{ borderRadius: '6px', borderColor: '#e0e0e0', background: '#f8f9fa', fontSize: '0.9rem' }}
                      />
                      <small className="text-muted">Auto-generated</small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="itemNo" className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Item No. <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control form-control-sm ${errors.itemNo ? 'is-invalid' : ''}`}
                        id="itemNo"
                        name="itemNo"
                        value={formData.itemNo}
                        onChange={handleChange}
                        placeholder="Enter item number"
                        style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                      />
                      {errors.itemNo && <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>{errors.itemNo}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Category <span className="text-danger">*</span></label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className={`form-select form-select-sm ${errors.category ? 'is-invalid' : ''}`}
                        style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                      >
                        <option value="">Select Category</option>
                        {(submasters['category'] || []).map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                      {errors.category && <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>{errors.category}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Product Name <span className="text-danger">*</span></label>
                      <select
                        name="productName"
                        value={formData.productName}
                        onChange={handleChange}
                        className={`form-select form-select-sm ${errors.productName ? 'is-invalid' : ''}`}
                        style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                      >
                        <option value="">Select Product Name</option>
                        {(submasters['product-name'] || []).map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                      {errors.productName && <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>{errors.productName}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>HS Code <span className="text-danger">*</span></label>
                      <select
                        name="hsCode"
                        value={formData.hsCode}
                        onChange={handleChange}
                        className={`form-select form-select-sm ${errors.hsCode ? 'is-invalid' : ''}`}
                        style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                      >
                        <option value="">Select HS Code</option>
                        {(submasters['hs-code'] || []).map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                      {errors.hsCode && <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>{errors.hsCode}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Specifications */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderTop: '4px solid #e74c3c' }}>
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0" style={{ color: '#e74c3c', fontWeight: '600' }}>
                <FaFileAlt className="me-2" />
                Product Specifications
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-6">
                  <TagInput
                    label="Materials"
                    selectedIds={formData.materials}
                    allItems={submasters['materials'] || []}
                    fieldName="materials"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Suitable For"
                    selectedIds={formData.suitableFor}
                    allItems={submasters['suitable-for'] || []}
                    fieldName="suitableFor"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Collection"
                    selectedIds={formData.collection}
                    allItems={submasters['collection'] || []}
                    fieldName="collection"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Color"
                    selectedIds={formData.color}
                    allItems={submasters['color'] || []}
                    fieldName="color"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                    required
                    errors={errors.color}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Finish"
                    selectedIds={formData.finish}
                    allItems={submasters['finish'] || []}
                    fieldName="finish"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                    required
                    errors={errors.finish}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Pack Type"
                    selectedIds={formData.packType}
                    allItems={submasters['pack-type'] || []}
                    fieldName="packType"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                    required
                    errors={errors.packType}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Dimensions & Weight */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderTop: '4px solid #f39c12' }}>
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0" style={{ color: '#f39c12', fontWeight: '600' }}>
                <FaCube className="me-2" />
                Dimensions & Weight
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 col-6 mb-3">
                  <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Length <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.lengthDimension ? 'is-invalid' : ''}`}
                    name="lengthDimension"
                    value={formData.lengthDimension}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                  />
                  {errors.lengthDimension && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{errors.lengthDimension}</div>}
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Breadth <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.breadthDimension ? 'is-invalid' : ''}`}
                    name="breadthDimension"
                    value={formData.breadthDimension}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                  />
                  {errors.breadthDimension && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{errors.breadthDimension}</div>}
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Height <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.heightDimension ? 'is-invalid' : ''}`}
                    name="heightDimension"
                    value={formData.heightDimension}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                  />
                  {errors.heightDimension && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{errors.heightDimension}</div>}
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Unit</label>
                  <select
                    name="dimensionUnit"
                    value={formData.dimensionUnit}
                    onChange={handleChange}
                    className="form-select form-select-sm"
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                  >
                    <option value="cm">cm</option>
                    <option value="inch">inch</option>
                    <option value="mm">mm</option>
                    <option value="m">m</option>
                  </select>
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Net Weight <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.netWeight ? 'is-invalid' : ''}`}
                    name="netWeight"
                    value={formData.netWeight}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                  />
                  {errors.netWeight && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{errors.netWeight}</div>}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Weight Unit</label>
                  <select
                    name="weightUnit"
                    value={formData.weightUnit}
                    onChange={handleChange}
                    className="form-select form-select-sm"
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                  >
                    <option value="KG">KG</option>
                    <option value="lbs">lbs</option>
                    <option value="g">g</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & Logistics */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderTop: '4px solid #2980b9' }}>
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0" style={{ color: '#2980b9', fontWeight: '600' }}>
                <FaWeight className="me-2" />
                Pricing & Logistics
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>MOQ (Minimum Order Quantity) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.moq ? 'is-invalid' : ''}`}
                    name="moq"
                    value={formData.moq}
                    onChange={handleChange}
                    placeholder="Enter MOQ"
                    min="1"
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                  />
                  {errors.moq && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{errors.moq}</div>}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label fw-600" style={{ color: '#2c3e50', fontSize: '0.9rem' }}>Cost <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.cost ? 'is-invalid' : ''}`}
                    name="cost"
                    value={formData.cost}
                    onChange={handleChange}
                    placeholder="Enter cost"
                    step="0.01"
                    min="0"
                    style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
                  />
                  {errors.cost && <div className="invalid-feedback d-block" style={{ fontSize: '0.75rem' }}>{errors.cost}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Inner Pack */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderTop: '4px solid #3498db' }}>
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0" style={{ color: '#3498db', fontWeight: '600' }}>
                <FaCube className="me-2" />
                Inner Pack Details
              </h5>
            </div>
            <div className="card-body" style={{ overflowX: 'auto' }}>
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead style={{ background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Quantity Pack</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Length</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Breadth</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Height</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Net Wt</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0', width: '50px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.innerPacks && formData.innerPacks.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g., 12 pcs per inner"
                            value={row.quantityPack}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'quantityPack', e.target.value)}
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.length}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'length', e.target.value)}
                            step="0.01"
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.breadth}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'breadth', e.target.value)}
                            step="0.01"
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.height}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'height', e.target.value)}
                            step="0.01"
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.netWt}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'netWt', e.target.value)}
                            step="0.01"
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => removePackRow('innerPacks', idx)}
                            disabled={formData.innerPacks.length === 1}
                            style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                          >
                            <FaTrash size={10} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => addPackRow('innerPacks')}
                style={{ borderRadius: '6px', marginTop: '10px', fontSize: '0.85rem' }}
              >
                <FaPlus className="me-2" size={10} /> Add Row
              </button>
            </div>
          </div>

          {/* Master Pack */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderTop: '4px solid #27ae60' }}>
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0" style={{ color: '#27ae60', fontWeight: '600' }}>
                <FaWeight className="me-2" />
                Master Pack Details
              </h5>
            </div>
            <div className="card-body" style={{ overflowX: 'auto' }}>
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead style={{ background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Quantity Pack</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Length</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Breadth</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Height</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Net Wt</th>
                      <th style={{ padding: '8px', border: '1px solid #e0e0e0', width: '50px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.masterPacks && formData.masterPacks.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g., 48 pcs per master"
                            value={row.quantityPack}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'quantityPack', e.target.value)}
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.length}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'length', e.target.value)}
                            step="0.01"
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.breadth}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'breadth', e.target.value)}
                            step="0.01"
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.height}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'height', e.target.value)}
                            step="0.01"
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.netWt}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'netWt', e.target.value)}
                            step="0.01"
                            style={{ borderRadius: '4px', fontSize: '0.85rem' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => removePackRow('masterPacks', idx)}
                            disabled={formData.masterPacks.length === 1}
                            style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                          >
                            <FaTrash size={10} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() => addPackRow('masterPacks')}
                style={{ borderRadius: '6px', marginTop: '10px', fontSize: '0.85rem' }}
              >
                <FaPlus className="me-2" size={10} /> Add Row
              </button>
            </div>
          </div>

          {/* Remarks */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderTop: '4px solid #8e44ad' }}>
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0" style={{ color: '#8e44ad', fontWeight: '600' }}>
                <FaFileAlt className="me-2" />
                Additional Information
              </h5>
            </div>
            <div className="card-body">
              <label htmlFor="remark" className="form-label fw-600" style={{ color: '#2c3e50' }}>Remarks / Notes</label>
              <textarea
                className="form-control form-control-sm"
                id="remark"
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                rows="3"
                placeholder="Enter any additional remarks or notes"
                style={{ borderRadius: '6px', borderColor: '#e0e0e0', fontSize: '0.9rem' }}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mb-4 d-flex justify-content-between gap-2">
            <div className="d-flex gap-2">
              <button
                type="submit"
                disabled={saving || processingImages}
                className="btn"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', borderRadius: '6px', padding: '10px 30px', fontWeight: '600', border: 'none', fontSize: '0.9rem' }}
              >
                <FaCheckCircle className="me-2" />
                {saving ? (isEditMode ? 'Updating Product...' : 'Creating Product...') : (isEditMode ? 'Update Product' : 'Create Product')}
              </button>
              {isEditMode && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="btn"
                  style={{ background: '#dc3545', color: '#fff', borderRadius: '6px', padding: '10px 30px', fontWeight: '600', border: 'none', fontSize: '0.9rem' }}
                >
                  <FaTrash className="me-2" />
                  Delete Product
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductMaster;
